import { isFunction } from '@btime/helpers'
import { generateContext } from '@btime/react-contexts'
import { State, appendValue, moveTo, moveToRoot, setValue } from '@btime/state-monad'
import { navigate } from '@reach/router'
import { any, equals, init, last, propEq, slice, indexOf } from 'ramda'
import React, { useCallback, useContext, useReducer } from 'react'

import { useNavigation } from '../../utils/contexts/NavigationContext'
import { getJsonQueryParam, prepareJsonQueryParams } from '../../utils/helpers/url'
import { hasValue } from '../../utils/helpers/value'
import { handleState } from '../../utils/reducer/basicStateHandler'
import { Reducer } from '../../utils/reducer/chain'

const ADD = 'ADD'
const ADD_OUTSIDE = 'ADD_OUTSIDE'
const CLEAR = 'CLEAR'
const CLEAR_CURRENT = 'CLEAR_CURRENT'
const GO_TO = 'GO_TO'

const { StateContext, ActionsContext, Provider } = generateContext()

export const useModalsState = () => useContext(StateContext) || initialState

export const useCurrentModal = () => {
  const { index, history, outsideHistory } = useModalsState()
  return hasValue(outsideHistory) ? last(outsideHistory) : history[index] || {}
}

export const useIsInModal = () => {
  const { history, outsideHistory } = useModalsState() || {}
  return !!history?.length || !!outsideHistory?.length
}

export const useCloseModalOrNavigation = () => {
  const closeModal = useCloseModal()
  const isModal = useIsInModal()
  const navigate = useNavigation()

  return path => {
    if (isModal) {
      closeModal()
    } else {
      navigate(path)
    }
  }
}

export const useCloseModalOrShowMessage = () => {
  const closeModal = useCloseModal()
  const isModal = useIsInModal()
  const navigate = useNavigation()

  return (title, description) => {
    if (isModal) {
      closeModal()
    } else {
      navigate(`/public/message?title=${title}&description=${description}`)
    }
  }
}

export const useModalData = () => {
  const { data } = useCurrentModal()
  return data
}

export const useIsModalFocused = name => {
  const { index, history, outsideHistory } = useModalsState()
  const current = last(outsideHistory) || history[index] || {}
  return current?.name === name
}

export const useIsOutsideModal = name => {
  const { outsideHistory } = useModalsState()
  const current = last(outsideHistory) || {}
  return current?.name === name
}

export const useIsModalOpen = name => {
  const { index, history, outsideHistory } = useModalsState()
  const current = history[index] || {}

  return current?.name === name || outsideHistory?.some(propEq('name', name))
}

export const useCanMoveForward = () => {
  const state = useModalsState()
  return Boolean(state.history[state.index + 1])
}

export const useCanMoveBack = () => {
  const state = useModalsState()
  return Boolean(state.history[state.index - 1])
}

export const useOpenModal = () => {
  const { onAdd, onAddOutside, onClear } = useModalsActions()
  const navigate = useNavigation()
  return (name, data, label, options = {}) => {
    if (data?.target) data = undefined
    const { clearHistory, outside, disableUrl, ignoreQueryParams } = options
    const queryParams = ignoreQueryParams ? '' : prepareJsonQueryParams('modal', { name, data, label })
    if (!disableUrl) navigate(`?${queryParams}`)
    if (clearHistory) onClear()
    const handleAdd = outside ? onAddOutside : onAdd
    handleAdd({ name, data, label })
  }
}

export const useMoveBack = () => {
  const { onGoBack } = useModalsActions()
  return () => {
    onGoBack()
  }
}

export const useCloseModal = () => {
  const data = useModalData()
  const { onClearCurrent } = useModalsActions()
  return () => {
    const qp = prepareJsonQueryParams('modal', undefined)
    navigate(`?${qp}`)
    onClearCurrent()
    if (isFunction(data?.onClose)) data.onClose()
  }
}

export const useModalsActions = () => useContext(ActionsContext)

const initialState = { index: -1, history: [], outsideHistory: [] }

export const reducer = (state, action) => {
  return Reducer(state, action)
    .map(
      handleState(ADD, (state, action) => {
        const newIndex = state.index + 1
        const historyAlreadyExist = any(history => equals(history, action.payload), state.history)

        if (historyAlreadyExist) {
          const alreadyExistIndex = indexOf(action.payload, state.history)
          return State.of(state).map(moveTo('index')).map(setValue(alreadyExistIndex)).fold()
        }

        return State.of(state)
          .map(moveTo('history'))
          .map(setValue(slice(0, newIndex)))
          .map(appendValue(action.payload))

          .map(moveToRoot())
          .map(moveTo('index'))
          .map(setValue(newIndex))

          .fold()
      })
    )

    .map(
      handleState(ADD_OUTSIDE, (state, action) => {
        return State.of(state) //
          .map(moveTo('outsideHistory'))
          .map(appendValue(action.payload))
          .fold()
      })
    )

    .map(
      handleState(GO_TO, (state, action) => {
        const newIndex = action.payload

        if (newIndex < 0) return initialState
        if (!state.history[newIndex]) return state

        return State.of(state) //
          .map(moveTo('index'))
          .map(setValue(newIndex))
          .fold()
      })
    )

    .map(
      handleState(CLEAR_CURRENT, (state, action) => {
        const { outsideHistory, history } = state
        if (hasValue(outsideHistory)) {
          return State.of(state) //
            .map(moveTo('outsideHistory'))
            .map(setValue(init(outsideHistory)))
            .fold()
        }
        return initialState
      })
    )

    .map(
      handleState(CLEAR, (state, action) => {
        return initialState
      })
    )

    .foldState()
}

export const useModalsReducer = current => {
  const stateFromUrl = React.useMemo(() => getJsonQueryParam('modal'), [])
  const [state, dispatch] = useReducer(
    reducer,
    stateFromUrl ? { ...initialState, index: 0, history: [stateFromUrl] } : initialState
  )

  const onAdd = useCallback(payload => dispatch({ payload, type: ADD }), [dispatch])
  const onAddOutside = useCallback(payload => dispatch({ payload, type: ADD_OUTSIDE }), [dispatch])
  const onGoBack = useCallback(() => dispatch({ payload: state.index - 1, type: GO_TO }), [dispatch, state.index])
  const onGoForward = useCallback(() => dispatch({ payload: state.index + 1, type: GO_TO }), [dispatch, state.index])
  const onClearCurrent = useCallback(payload => dispatch({ payload, type: CLEAR_CURRENT }), [dispatch])
  const onClear = useCallback(payload => dispatch({ payload, type: CLEAR }), [dispatch])
  const onGoTo = useCallback(payload => dispatch({ payload, type: GO_TO }), [dispatch])

  return [state, { onAdd, onAddOutside, onGoBack, onGoForward, onClearCurrent, onClear, onGoTo }]
}

export const ModalsHandler = ({ children, ...props }) => {
  const [state, actions] = useModalsReducer()

  return (
    <Provider state={state} actions={actions}>
      {children}
    </Provider>
  )
}
